import React, { useContext } from 'react';
import { PapiComment } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { CommentEditorSource } from '../../../CommentEditor/redux/types';
import {
  CommentEditorProviders,
  CommentEditorProviderProps,
} from '../../../CommentEditor/context';
import CommentBoxRoot from '../../lego/CommentBoxRoot';

export interface Props
  extends Omit<
    CommentEditorProviderProps,
    'commentBoxId' | 'relevantComment' | 'editorSource'
  > {
  id: string;

  /** Shown if the user is logged out. */
  loggedOutView: React.ReactNode;

  /** Shown the user can comment. */
  editorView: React.ReactNode;

  /** Shown if the user is logged in, but can't comment for whatever reason. */
  cantPostView: React.ReactNode;

  className?: string;
  editComment?: PapiComment;
  replyComment?: PapiComment;

  canPostComment?: boolean;
  isSmall?: boolean;
}

/**
 * A skeleton of our CommentBox that includes the `<CommentEditorProviders />`
 * and some customizable view slots that change visibility based on the comment editor state.
 */
const CommentBoxSkeleton = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      id,

      loggedOutView,
      editorView,
      cantPostView,

      editComment,
      replyComment,
      isSmall,
      canPostComment = true,
      className,
      ...rest
    },
    ref
  ) => {
    const { isLoggedIn } = useContext(SessionContext);

    return (
      <CommentEditorProviders
        commentBoxId={id}
        editorSource={
          editComment
            ? CommentEditorSource.Edit
            : replyComment
            ? CommentEditorSource.Reply
            : CommentEditorSource.Independent
        }
        relevantComment={editComment || replyComment}
        {...rest}
      >
        <CommentBoxRoot className={className} isSmall={isSmall} ref={ref}>
          {!isLoggedIn && loggedOutView}

          {isLoggedIn && (canPostComment ? editorView : cantPostView)}
        </CommentBoxRoot>
      </CommentEditorProviders>
    );
  }
);
CommentBoxSkeleton.displayName = 'CommentBoxSkeleton';

export default CommentBoxSkeleton;
